/**
 * KnobField — one registry-driven config input (WSRPDM-0014). The unit on
 * `ScheduleKnobDef` picks the control: `seconds`/`count` → a number
 * `TextField` (label + a live friendly-duration/unit hint + the raw key in
 * mono, per spec — seconds knobs reuse `formatSecondsDuration` from the
 * Queue screen, WSRPDM-0013, rather than a second formatter); `enum`
 * (`isolation`) → a `TextField select` (the 5 isolation values); `bool`
 * (`skipLockedClaim`) → `AppSwitch`. Adding a knob to the registry needs no
 * change here — this is the "one registry entry, no per-knob JSX" seam.
 */
import { Box, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { AppSwitch, HelpPopover } from '@/components/ui';
import { formatSecondsDuration } from '@/routes/queue/duration';
import {
  COUNT_MIN_ZERO,
  ENGINE_HELP_SCOPE_FALLBACK,
  humanizeKnobKey,
  ISOLATION_VALUES,
  type ScheduleKnobDef,
} from '@/domain/scheduleKnobs';

interface KnobFieldProps {
  def: ScheduleKnobDef;
  value: string | boolean;
  /** Field-level error — from client validation or the server's `field` (contract §2.2). */
  error?: string | undefined;
  /** True when this field is one of the 3 highlighted by an invariant failure (no server `field`). */
  invariant?: boolean | undefined;
  onChange: (value: string | boolean) => void;
}

/**
 * Seconds knobs render a live FRIENDLY duration (e.g. "1h", "90s") next to
 * the raw editable value, reusing `formatSecondsDuration` from the Queue
 * screen (WSRPDM-0013) rather than a second formatter — per the spec's
 * "durations are integer seconds — render friendly units" (Background).
 * Falls back to the raw "seconds" unit word while the field is empty/NaN
 * mid-edit (e.g. the user just cleared it).
 */
function unitHint(def: ScheduleKnobDef, value: string, t: ReturnType<typeof useCopy>['t']): string {
  if (def.unit === 'seconds') {
    const n = Number(value);
    return Number.isFinite(n) && value.trim() !== ''
      ? formatSecondsDuration(n)
      : t('engine.unit.seconds', 'seconds');
  }
  if (def.unit === 'count') return t('engine.unit.count', 'count');
  return '';
}

export function KnobField({ def, value, error, invariant, onChange }: KnobFieldProps) {
  const { t } = useCopy();
  const label = t(def.labelKey, humanizeKnobKey(def.key));
  const hasError = Boolean(error) || Boolean(invariant);
  const detail = t(`engine.fields.${def.key}.detail`, '');
  const emphasis = def.emphasisKey ? t(def.emphasisKey, '') : '';
  const fieldId = `engine-${def.key}`;
  const helperMessage =
    error ??
    (invariant
      ? t('engine.validation.invariantHint', 'Part of the heartbeat/lease/max-execution invariant.')
      : undefined);

  const restartBadge = def.restartRequired ? (
    <Chip
      size="small"
      label={t('engine.restartRequired', 'Restart required')}
      color="warning"
      variant="outlined"
      sx={{ mt: 0.5 }}
    />
  ) : null;

  const helpPopover = detail ? (
    <HelpPopover
      label={label}
      scope={t(`help.scope.${def.helpScope}`, ENGINE_HELP_SCOPE_FALLBACK[def.helpScope])}
      body={detail}
    />
  ) : null;

  const emphasisCaption = emphasis ? (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="engine-emphasis"
      sx={{ display: 'block', mt: 0.75 }}
    >
      {emphasis}
    </Typography>
  ) : null;

  if (def.unit === 'bool') {
    return (
      <Box>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <AppSwitch label={label} checked={value as boolean} onChange={onChange} />
          {helpPopover}
        </Stack>
        {emphasisCaption}
        {restartBadge}
      </Box>
    );
  }

  if (def.unit === 'enum') {
    return (
      <Box>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2">{label}</Typography>
          {helpPopover}
        </Stack>
        <TextField
          id={fieldId}
          select
          slotProps={{ select: { 'aria-label': label } }}
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          error={hasError}
          helperText={
            helperMessage ?? (
              <Box component="code" sx={{ fontFamily: 'monospace' }}>
                {def.key}
              </Box>
            )
          }
          size="small"
          fullWidth
        >
          {ISOLATION_VALUES.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        {emphasisCaption}
        {restartBadge}
      </Box>
    );
  }

  // seconds | count
  const min = def.unit === 'count' && COUNT_MIN_ZERO.includes(def.key) ? 0 : 1;
  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography component="label" htmlFor={fieldId} variant="body2">
          {label}
        </Typography>
        {helpPopover}
      </Stack>
      <TextField
        id={fieldId}
        type="number"
        value={value as string}
        onChange={(e) => onChange(e.target.value)}
        error={hasError}
        helperText={
          helperMessage ?? (
            <Box component="span" sx={{ display: 'inline-flex', gap: 0.75, alignItems: 'center' }}>
              <Typography component="span" variant="caption">
                {unitHint(def, value as string, t)}
              </Typography>
              <Box component="code" sx={{ fontFamily: 'monospace' }}>
                {def.key}
              </Box>
            </Box>
          )
        }
        size="small"
        fullWidth
        slotProps={{ htmlInput: { min, step: 1 } }}
      />
      {emphasisCaption}
      {restartBadge}
    </Box>
  );
}
